import type { MarketSignal, SignalKind } from "@/types/marketSignal.type";
import { EVERGREEN_CANDIDATE_KINDS, SIGNAL_KINDS } from "@/types/marketSignal.type";

/**
 * Kind reads as a monochrome glyph plus a Manrope label. The six-hex brand
 * palette never encodes kinds as colours, so no kind owns a hue: colour on this
 * surface is reserved for horizon STATE (see HORIZON_STATE_COLORS) and the
 * accent.
 *
 * `label` is the plural the page owns, `singular` the one-card form. Both are
 * written out rather than derived, because "Identities" does not lose an "s"
 * to become "Identity".
 */
export const KIND_META: Record<string, { label: string; singular: string; blurb: string }> = {
  trend: { label: "Trends", singular: "Trend", blurb: "What's surging right now" },
  tone_shift: { label: "Tone shifts", singular: "Tone shift", blurb: "How the brand is sounding" },
  positioning_shift: {
    label: "Positioning shifts",
    singular: "Positioning shift",
    blurb: "Where the brand is moving",
  },
  platform_shift: {
    label: "Platform shifts",
    singular: "Platform shift",
    blurb: "Channel-level changes",
  },
  cultural_moment: {
    label: "Cultural moments",
    singular: "Cultural moment",
    blurb: "Moments to ride",
  },
  category_shift: {
    label: "Category shifts",
    singular: "Category shift",
    blurb: "The category is moving",
  },
  audience_shift: {
    label: "Audience shifts",
    singular: "Audience shift",
    blurb: "Who we're reaching",
  },
  /* Filed by the field reporter; never offered when a person logs a signal. */
  identity: { label: "Identities", singular: "Identity", blurb: "Who the brand durably is" },
  competitive_pattern: {
    label: "Competitive patterns",
    singular: "Competitive pattern",
    blurb: "What competitors keep doing",
  },
  active_campaign: {
    label: "Active campaigns",
    singular: "Active campaign",
    blurb: "A campaign running in the market",
  },
};

/** An unknown kind in sentence case, never with underscores ("brand_voice" -> "Brand voice"). */
function humanizeKind(kind: string): string {
  const words = kind.split(/[_\s]+/).filter(Boolean).join(" ").toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Signal";
}

export function kindLabel(kind: string): string {
  return KIND_META[kind]?.label ?? humanizeKind(kind);
}

/** Singular label for a single card (Trends -> Trend). Unknown kinds are never trimmed. */
export function kindLabelSingular(kind: string): string {
  return KIND_META[kind]?.singular ?? humanizeKind(kind);
}

/** The only kinds where "until superseded" (validUntil = null) is allowed. */
export function isEvergreenKind(kind: string): boolean {
  return EVERGREEN_CANDIDATE_KINDS.includes(kind as SignalKind);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/* ------------------------------------------------------------------ dates */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A date-only string ("2026-10-24") is a calendar day, so it is read as local
 *  midnight rather than UTC midnight, which would print the day before west of
 *  Greenwich. Everything else is an instant. */
export function parseDate(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(iso);
}

/** "8 Sep", with the year only when it is not the current one ("3 Jan 2027").
 *  Months are written out rather than asked of Intl, which prints "Sept" for en-GB. */
export function formatShortDate(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "";
  const d = parseDate(iso);
  if (Number.isNaN(d.getTime())) return "";
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** "11 Sep 2026": the ledger form, always with the year. */
export function formatFullDate(iso: string | null): string {
  if (!iso) return "";
  const d = parseDate(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Month abbreviation for a date, for axis labels ("Oct"). */
export function monthLabel(d: Date): string {
  return MONTHS[d.getMonth()];
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Whole calendar days from today to the given date: 0 is today, negative is past. */
export function calendarDaysUntil(iso: string, now: Date = new Date()): number {
  return Math.round((startOfDay(parseDate(iso)) - startOfDay(now)) / MS_PER_DAY);
}

/** True when two instants fall on the same local calendar day. */
export function isSameDay(a: string, b: string): boolean {
  return startOfDay(parseDate(a)) === startOfDay(parseDate(b));
}

export function weeksLabel(weeks: number): string {
  return weeks === 1 ? "1 week" : `${weeks} weeks`;
}

export function daysLabel(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/** A duration in the words a curator uses: days under a week, whole weeks after. */
function durationFromDays(days: number): string {
  if (days < 7) return daysLabel(Math.max(1, days));
  return weeksLabel(Math.round(days / 7));
}

/* ---------------------------------------------------------------- horizon */

export interface Horizon {
  /** null = "until superseded" (open-ended). */
  daysLeft: number | null;
  totalDays: number | null;
  /** 0..1 fraction of the window remaining (1 = fresh, 0 = expiring). */
  fractionLeft: number;
  label: string;
  expired: boolean;
  openEnded: boolean;
}

/** Compute a readable horizon + a progress fraction for a signal's window. */
export function computeHorizon(signal: MarketSignal, now: Date = new Date()): Horizon {
  if (!signal.validUntil) {
    return {
      daysLeft: null,
      totalDays: null,
      fractionLeft: 1,
      label: "Until superseded",
      expired: false,
      openEnded: true,
    };
  }
  const until = parseDate(signal.validUntil).getTime();
  const from = parseDate(signal.validFrom).getTime();
  const total = Math.max(1, until - from);
  const left = until - now.getTime();
  const daysLeft = Math.ceil(left / MS_PER_DAY);
  const fractionLeft = Math.max(0, Math.min(1, left / total));
  const expired = left <= 0;

  let label: string;
  if (expired) {
    label = "Expired";
  } else if (daysLeft <= 1) {
    label = "Expires today";
  } else if (daysLeft < 14) {
    label = `${daysLeft} days left`;
  } else if (daysLeft < 60) {
    label = `${weeksLabel(Math.round(daysLeft / 7))} left`;
  } else {
    label = `${Math.round(daysLeft / 30)} months left`;
  }

  return {
    daysLeft,
    totalDays: Math.round(total / MS_PER_DAY),
    fractionLeft,
    label,
    expired,
    openEnded: false,
  };
}

/**
 * Horizon presentation STATE.
 *
 *   healthy     = Status Green, exact (#04E735 via --co-state-live) in both
 *                 themes: alive and running. The days-left figure carries the
 *                 meaning; the bar only confirms it.
 *   approaching = the accent: "this one needs you", the same tone every other
 *                 call to attention on this surface uses (Blue on light, Lime
 *                 on dark).
 *   expired     = muted: inert, not an error. A window closing is designed
 *                 behaviour, so it reads past tense rather than alarming.
 *
 * No other hue enters this scale: one live colour, the accent for attention,
 * neutral for done.
 */
export type HorizonState = "healthy" | "approaching" | "expired";

/** Token colour for each horizon state (drives the steering runway). */
export const HORIZON_STATE_COLORS: Record<HorizonState, string> = {
  healthy: "var(--co-state-live)",
  approaching: "var(--co-accent)",
  expired: "var(--co-muted)",
};

/**
 * A window is "approaching" once it is inside two weeks OR its runway has
 * drained past three quarters. Open-ended (until superseded) always reads healthy.
 */
const APPROACHING_DAYS = 14;
const APPROACHING_FRACTION = 0.25;

/** Map a computed horizon to its presentation state. */
export function horizonState(horizon: Horizon): HorizonState {
  if (horizon.expired) return "expired";
  if (horizon.openEnded) return "healthy";
  if (
    (horizon.daysLeft != null && horizon.daysLeft <= APPROACHING_DAYS) ||
    horizon.fractionLeft <= APPROACHING_FRACTION
  ) {
    return "approaching";
  }
  return "healthy";
}

/** Approximate human duration between now and an ISO date, e.g. "~6 weeks".
 *  A date already behind us reads "ended", never "~1 day". */
export function approxDuration(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "until superseded";
  const ms = parseDate(iso).getTime() - now.getTime();
  if (ms <= 0) return "ended";
  const days = Math.ceil(ms / MS_PER_DAY);
  if (days <= 1) return "~1 day";
  if (days < 14) return `~${days} days`;
  if (days < 60) return `~${weeksLabel(Math.round(days / 7))}`;
  return `~${Math.round(days / 30)} months`;
}

/** Convert a week count (from now) to an ISO expiry date. */
export function validUntilFromWeeks(weeks: number, now: Date = new Date()): string {
  const clamped = Math.max(1, Math.floor(weeks));
  return new Date(now.getTime() + clamped * 7 * MS_PER_DAY).toISOString().slice(0, 10);
}

/* ------------------------------------------------------------ accept window */

/** The kind default when there is no usable suggestion. */
export const DEFAULT_WINDOW_WEEKS = 4;

export interface AcceptWindow {
  /** What Accept sends: a future date, or null for "until superseded". */
  validUntil: string | null;
  /** The stored suggestion is already in the past. */
  stale: boolean;
  /** The past suggestion, when stale. */
  staleDate: string | null;
  /** The same window in whole weeks (min 1), or null when open-ended. */
  weeks: number | null;
  /** "3 weeks", "5 days", "1 day" or "until superseded". */
  durationLabel: string;
  buttonLabel: string;
  sentence: string;
}

/**
 * The window a pending signal would run for if accepted right now.
 *
 * A suggestion in the future is used as is. A suggestion already behind us is
 * NOT sent: the API rejects a validUntil in the past, so Accept would fail. It falls back
 * to the kind default instead (until superseded for the evergreen kinds, four
 * weeks for everything else) and says so in the sentence. No suggestion at all
 * takes the same default without the explanation.
 */
export function acceptWindow(signal: MarketSignal, now: Date = new Date()): AcceptWindow {
  const evergreen = isEvergreenKind(signal.kind);
  const suggested = signal.recommendedValidUntil;

  if (suggested && parseDate(suggested).getTime() > now.getTime()) {
    const days = Math.max(1, calendarDaysUntil(suggested, now));
    const duration = durationFromDays(days);
    return {
      validUntil: suggested,
      stale: false,
      staleDate: null,
      weeks: Math.max(1, Math.round(days / 7)),
      durationLabel: duration,
      buttonLabel: `Accept for ${duration}`,
      sentence: `Runs until ${formatShortDate(suggested, now)} as suggested, about ${duration} once accepted.`,
    };
  }

  const fallbackUntil = evergreen ? null : validUntilFromWeeks(DEFAULT_WINDOW_WEEKS, now);
  const defaultDuration = weeksLabel(DEFAULT_WINDOW_WEEKS);
  const base = {
    validUntil: fallbackUntil,
    weeks: evergreen ? null : DEFAULT_WINDOW_WEEKS,
    durationLabel: evergreen ? "until superseded" : defaultDuration,
    buttonLabel: evergreen ? "Accept until superseded" : `Accept for ${defaultDuration}`,
  };
  const until = formatShortDate(fallbackUntil, now);

  if (suggested) {
    const ended = `The suggested window ended on ${formatShortDate(suggested, now)}.`;
    return {
      ...base,
      stale: true,
      staleDate: suggested,
      sentence: evergreen
        ? `${ended} Accepting runs it until a newer signal replaces it.`
        : `${ended} Accepting runs it ${defaultDuration}, until ${until}.`,
    };
  }

  return {
    ...base,
    stale: false,
    staleDate: null,
    sentence: evergreen
      ? "Runs until a newer signal replaces it."
      : `Runs ${defaultDuration} once accepted, until ${until}.`,
  };
}

/** The seven kinds a person may log. The reporter-only kinds are never offered. */
export const SIGNAL_KIND_OPTIONS: { value: SignalKind; label: string }[] = SIGNAL_KINDS.map(
  (k) => ({ value: k, label: kindLabelSingular(k) }),
);
